import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useAuth } from "@clerk/nextjs";

import SocialLinks from "components/links/SocialLinks/SocialLinks";
import useWindowSize from "lib/hooks/useWindowSize";

import FooterColumns from "./FooterColumns";
import styles from "./Footer.module.scss";

const JoinUs = dynamic(() => import("components/JoinUs"));

const COLUMNS_DATA = {
  forCopyRight:
    `© Copyright ${new Date().getFullYear()} WhereNext Experiences Pty. Ltd.  All rights reserved.`,
  forTandC: "Terms | Privacy\n\n",
  logoDescription:
    "Travel experiences with a focus on volunteering and animal conservation",
  column1: {
    text: "",
    title: "For business",
    links: [
      {
        link: "/about/partner-with-us",
        linkName: "Partner with Us",
      },
      {
        link: "/about/corporate-social-responsibility",
        linkName: "Corporate Social Responsibility",
      },
      {
        link: "/contact",
        linkName: "Contact Us",
      },
    ],
  },
  column2: {
    text: "",
    title: "Our charities",
    links: [
      {
        link: "/about/one-percent-for-the-planet",
        linkName: "1% for the Planet",
      },
      {
        link: "https://pledge1percent.org/",
        linkName: "Pledge 1%",
        target: "_blank",
      },
      {
        link: "https://www.kiva.org/",
        linkName: "Kiva",
        target: "_blank",
      },
      {
        link: "/about/one-tree-planted",
        linkName: "One Tree Planted",
      },
    ],
  },
  column3: {
    text: "We partner with travel providers and charities that give back to local communities.",
    title: "Our partners",
  },
  column4: {
    text: "",
    title: "Categories",
    links: [
      {
        link: "/experiences/adventure",
        linkName: "Adventure",
      },
      {
        link: "/experiences/animals",
        linkName: "Animals",
      },
      {
        link: "/experiences/internships",
        linkName: "Internships",
      },
      {
        link: "/experiences/learning",
        linkName: "Learning",
      },
      {
        link: "/experiences/teaching",
        linkName: "Teaching",
      },
      {
        link: "/experiences/volunteering",
        linkName: "Volunteering",
      },
      {
        link: "/experiences/yoga",
        linkName: "Yoga",
      },
    ],
  },
};

const Footer = ({ showEmailSignup = true, footerData, showFooter }) => {
  const { isSignedIn } = useAuth();
  const windowSize = useWindowSize();
  const router = useRouter();

  if (!showFooter?.mobile && windowSize?.width < 768) {
    return null;
  }

  return (
    <>
      {showEmailSignup && !isSignedIn && <JoinUs />}
      <div className={`${styles.footerArea}`}>
        <div className="variable-container">
          <div className={`${styles.footerRow}`}>
            <div className={`${styles.column} ${styles.logoColumn}`}>
              <Link href="/" aria-label="home">
                <Image
                  width={123}
                  height={24}
                  src={"/assets/img/logo_full.svg"}
                  alt="WhereNext Logo"
                />
              </Link>
              <div className={`${styles.logoDescription}`}>
                <p>{COLUMNS_DATA?.logoDescription}</p>
              </div>
              <div className={`${styles.socialContacts}`}>
                <span>Let’s chat:</span>
                <SocialLinks
                  include={["messenger", "whatsapp"]}
                  iconColor="var(--light-text"
                  links={footerData?.socialLinks}
                  spacing="4px"
                />
              </div>
            </div>
            <FooterColumns footerData={COLUMNS_DATA} />
          </div>

          <div className={`${styles.bottomBar}`}>
            <div className={`${styles.tcLinks}`}>
              <Link href="/terms" aria-label="Terms">
                Terms
              </Link>
              &nbsp; | &nbsp;
              <Link href="/privacy" aria-label="Privacy">
                Privacy
              </Link>
            </div>
            <div className={`${styles.centerSection}`}>
              <div className={`${styles.wherenextAiLink}`}>
                <Link 
                  href="https://wherenext.ai/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="Create your free Travel Guide with WhereNext Ai"
                >
                  Create your free Travel Guide with WhereNext Ai
                </Link>
              </div>
              <p className={`${styles.copyright}`}>
                {COLUMNS_DATA?.forCopyRight}
              </p>
            </div>
            <div className={`${styles.socialLinks}`}>
              <SocialLinks
                include={["facebook", "instagram", "twitter", "linkedin"]}
                iconColor="var(--light-text"
                links={footerData?.socialLinks}
              />
            </div>
          </div>

          {router?.pathname === "/" && (
            <div className={`${styles.startupFameRow}`}>
              <Link 
                href="https://startupfa.me/s/wherenext?utm_source=www.wherenext.to" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="WhereNext Experience - Featured on Startup Fame"
              >
                <img 
                  src="https://startupfa.me/badges/featured/default.webp" 
                  alt="WhereNext - Featured on Startup Fame" 
                  width="171" 
                  height="54" 
                />
              </Link>
              <Link 
                href="https://twelve.tools" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Featured on Twelve Tools"
              >
                <img 
                  src="https://twelve.tools/badge0-white.svg" 
                  alt="Featured on Twelve Tools" 
                  width="200" 
                  height="54" 
                />
              </Link>
              <Link 
                href="https://wired.business" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Featured on Wired Business"
              >
                <img 
                  src="https://wired.business/badge0-white.svg" 
                  alt="Featured on Wired Business" 
                  width="200" 
                  height="54" 
                />
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Footer;
